import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { formatMarkdownToSafeHtml } from '../../utils/html';
import './SupportBotWidget.css';

const toSafeString = (value) => (
  typeof value === 'string' ? value : String(value ?? '')
);

const SupportBotChatSurface = ({
  isOpen = false,
  onToggle = () => {},
  onOpen = null,
  onCollapse = null,
  onRestore = null,
  loading = false,
  sending = false,
  errorText = '',
  welcomeMessage = '',
  messages = [],
  suggestedQuestions = [],
  onSuggestedQuestion = () => {},
  draft = '',
  onDraftChange = () => {},
  onSubmit = () => {},
  unreadCount = 0,
  botName = 'Assistente',
  avatarUrl = '',
  headerSubtitle = 'Assistente de suporte',
  toggleLabel = 'ajuda?',
  themeClassName = '',
  themeStyle = {},
  messagesContainerRef = null,
  showSuggestedQuestionsOnlyAtStart = false,
  closedDisplayMode = 'toggle',
  hideOpenToggle = false,
  showClosedDismissControl = false,
  collapsedAriaLabel = 'Mostrar atalho de suporte',
  collapsedTooltip = 'Mostrar atalho de ajuda',
  collapseTooltip = 'Recolher para a lateral',
  inputPlaceholder = 'Digite sua dúvida...',
  closeTooltip = 'Fechar chat',
  closeText = 'Fechar',
}) => {
  const handleOpen = onOpen || onToggle;
  const handleCollapse = onCollapse || onToggle;
  const handleRestore = onRestore || onToggle;
  const normalizedBotName = toSafeString(botName).trim() || 'Assistente';
  const normalizedMessages = Array.isArray(messages) ? messages : [];
  const normalizedSuggestedQuestions = Array.isArray(suggestedQuestions) ? suggestedQuestions : [];
  const normalizedWelcomeMessage = toSafeString(welcomeMessage).trim();
  const showFallbackWelcome = normalizedMessages.length === 0 && Boolean(normalizedWelcomeMessage);
  const hasUserStartedConversation = normalizedMessages.some(
    (message) => message?.role === 'user' && Boolean(toSafeString(message?.content).trim()),
  );
  const showSuggestedQuestions = (
    normalizedSuggestedQuestions.length > 0
    && (!showSuggestedQuestionsOnlyAtStart || !hasUserStartedConversation)
  );
  const showCollapsedHint = !isOpen && closedDisplayMode === 'sliver';
  const showToggleButton = !showCollapsedHint && (!isOpen || !hideOpenToggle);

  return (
    <Box className={`support-bot-widget ${themeClassName}`.trim()} style={themeStyle}>
      {isOpen && (
        <Paper elevation={12} className="support-bot-widget__panel">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="support-bot-widget__header"
          >
            <Stack direction="row" spacing={1} alignItems="center" className="support-bot-widget__header-main">
              <Avatar src={avatarUrl || undefined} alt={normalizedBotName} className="support-bot-widget__header-avatar">
                {normalizedBotName.slice(0, 1).toUpperCase() || 'A'}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>{normalizedBotName}</Typography>
                <Typography variant="caption" className="support-bot-widget__header-subtitle">
                  {toSafeString(headerSubtitle).trim() || 'Assistente de suporte'}
                </Typography>
              </Box>
            </Stack>
            <Tooltip title={closeTooltip}>
              <IconButton size="small" onClick={handleCollapse} className="support-bot-widget__header-close" aria-label={closeTooltip}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={1} className="support-bot-widget__content">
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center" className="support-bot-widget__loading-row">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Carregando suporte...
                </Typography>
              </Stack>
            )}

            {errorText && (
              <Alert severity="warning" className="support-bot-widget__error-alert">
                {errorText}
              </Alert>
            )}

            <Box ref={messagesContainerRef} className="support-bot-widget__messages-scroll">
              <Stack spacing={1}>
                {showFallbackWelcome ? (
                  <Box className="support-bot-widget__message support-bot-widget__message--assistant">
                    <Typography
                      variant="body2"
                      dangerouslySetInnerHTML={{ __html: formatMarkdownToSafeHtml(normalizedWelcomeMessage) }}
                    />
                  </Box>
                ) : null}
                {normalizedMessages.map((message, index) => {
                  const isAssistant = message?.role === 'assistant';
                  return (
                    <Box
                      key={message?.id || `msg-${index}`}
                      className={`support-bot-widget__message ${
                        isAssistant
                          ? 'support-bot-widget__message--assistant'
                          : 'support-bot-widget__message--user'
                      }`}
                    >
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: formatMarkdownToSafeHtml(toSafeString(message?.content).trim()),
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {showSuggestedQuestions && (
              <Box className="support-bot-widget__suggested">
                <Typography variant="caption" color="text.secondary">
                  Perguntas sugeridas
                </Typography>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" className="support-bot-widget__suggested-list">
                  {normalizedSuggestedQuestions.map((question) => (
                    <Chip
                      key={question}
                      label={question}
                      size="small"
                      onClick={() => onSuggestedQuestion(question)}
                      disabled={sending}
                      className="support-bot-widget__suggested-chip"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box component="form" onSubmit={onSubmit} className="support-bot-widget__form">
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={inputPlaceholder}
                  value={draft}
                  onChange={(event) => onDraftChange(event.target.value)}
                  disabled={sending}
                  className="support-bot-widget__text-field"
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={sending || !toSafeString(draft).trim()}
                  className="support-bot-widget__send-btn"
                >
                  {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}

      {showCollapsedHint ? (
        <Box className="support-bot-widget__sliver-wrap">
          <Badge
            color="error"
            badgeContent={unreadCount}
            invisible={!unreadCount}
            className="support-bot-widget__sliver-badge"
          >
            <Tooltip title={collapsedTooltip}>
              <Box
                component="button"
                type="button"
                onClick={handleRestore}
                aria-label={collapsedAriaLabel}
                className="support-bot-widget__sliver-button"
              >
                <Box className="support-bot-widget__sliver-glow" />
              </Box>
            </Tooltip>
          </Badge>
        </Box>
      ) : null}

      {showToggleButton ? (
        <Box className="support-bot-widget__toggle-wrap">
          <Badge color="error" badgeContent={unreadCount} invisible={!unreadCount}>
            <Button
              onClick={isOpen ? onToggle : handleOpen}
              variant="contained"
              className={`support-bot-widget__toggle-btn ${
                isOpen
                  ? 'support-bot-widget__toggle-btn--open'
                  : 'support-bot-widget__toggle-btn--closed'
              }`}
            >
              {isOpen ? (
                <Stack direction="row" spacing={0.7} alignItems="center" className="support-bot-widget__toggle-content">
                  <CloseIcon fontSize="small" />
                  <Typography variant="body2" className="support-bot-widget__toggle-label">
                    {closeText}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.8} alignItems="center" className="support-bot-widget__toggle-content">
                  <Avatar src={avatarUrl || undefined} alt={normalizedBotName} className="support-bot-widget__toggle-avatar">
                    {normalizedBotName.slice(0, 1).toUpperCase() || 'A'}
                  </Avatar>
                  <Typography variant="body2" className="support-bot-widget__toggle-label">
                    {toSafeString(toggleLabel).trim() || 'ajuda?'}
                  </Typography>
                </Stack>
              )}
            </Button>
          </Badge>
          {!isOpen && showClosedDismissControl ? (
            <Tooltip title={collapseTooltip}>
              <IconButton
                size="small"
                onClick={handleCollapse}
                className="support-bot-widget__toggle-dismiss"
                aria-label="Recolher suporte para a lateral"
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default SupportBotChatSurface;
